/**
 * Cardápio de tarefas (Administração) — spec tarefas-e-malha §3.
 * Lista, cria e edita os tipos de tarefa. Fontes (régua, auditoria) e a abertura
 * manual só abrem tarefas que existem aqui. Editar exige nível gestor no Kanban.
 */
import React, { useEffect, useState } from 'react';
import { ClipboardList, Plus, Edit3, Power, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { tiposTarefaAPI, setoresAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

const VAZIO = { codigo: '', nome: '', descricao: '', setor_padrao_id: '', prioridade_padrao: 'normal',
  campos: [], checklist: [], resultados: [], abertura_manual: true, ativo: true };
const TIPOS_CAMPO = [['text', 'Texto'], ['number', 'Número'], ['date', 'Data']];

export default function AdminCardapioTarefas() {
  const { temNivel } = useAuth();
  const podeEditar = temNivel('kanban', 'gestor');
  const [tipos, setTipos] = useState([]);
  const [setores, setSetores] = useState([]);
  const [verInativos, setVerInativos] = useState(false);
  const [form, setForm] = useState(null);      // null = modal fechado
  const [salvando, setSalvando] = useState(false);

  const carregar = () => {
    tiposTarefaAPI.list({ ativos: !verInativos }).then((r) => setTipos(r.data || [])).catch(() => toast.error('Falha ao carregar o cardápio'));
  };
  useEffect(() => { carregar(); }, [verInativos]);
  useEffect(() => { setoresAPI.list().then((r) => setSetores(r.data || [])).catch(() => {}); }, []);

  const abrir = (t) => setForm(t ? { ...VAZIO, ...t, setor_padrao_id: t.setor_padrao_id ?? '' } : { ...VAZIO });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setCampo = (i, k, v) => set('campos', form.campos.map((c, j) => (j === i ? { ...c, [k]: v } : c)));

  const salvar = async () => {
    if (!/^[a-z0-9_]{2,60}$/.test(form.codigo)) return toast.error('Código: só letras minúsculas, números e _ (ex.: dados_bancarios)');
    if (!form.nome.trim()) return toast.error('Informe o nome');
    if (form.campos.some((c) => !c.nome || !c.rotulo)) return toast.error('Todo campo precisa de nome e rótulo');
    setSalvando(true);
    try {
      const dados = { ...form, setor_padrao_id: form.setor_padrao_id ? Number(form.setor_padrao_id) : null,
        campos: form.campos.map((c) => ({ ...c, nome: c.nome.trim(), obrigatorio: !!c.obrigatorio })),
        checklist: form.checklist.map((s) => s.trim()).filter(Boolean),
        resultados: form.resultados.map((s) => s.trim()).filter(Boolean) };
      delete dados.id; delete dados.setor_padrao_nome;
      if (form.id) await tiposTarefaAPI.update(form.id, dados); else await tiposTarefaAPI.create(dados);
      toast.success('Cardápio atualizado');
      setForm(null);
      carregar();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao salvar');
    } finally {
      setSalvando(false);
    }
  };
  const alternarAtivo = async (t) => {
    try {
      if (t.ativo) await tiposTarefaAPI.desativar(t.id);
      else await tiposTarefaAPI.update(t.id, { ...t, ativo: true, setor_padrao_id: t.setor_padrao_id || null });
      carregar();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
  };

  const th = { textAlign: 'left', padding: '8px 10px', fontSize: '0.8rem', color: 'var(--gray-500)', borderBottom: '1px solid var(--gray-200)' };
  const td = { padding: '8px 10px', fontSize: '0.9rem', borderBottom: '1px solid var(--gray-100)', verticalAlign: 'top' };

  return (
    <div style={{ padding: '1.25rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
        <ClipboardList size={22} color="var(--brand, #20a490)" />
        <h2 style={{ margin: 0 }}>Cardápio de tarefas</h2>
        <label style={{ marginLeft: 'auto', fontSize: '0.85rem', display: 'flex', gap: 6, alignItems: 'center' }}>
          <input type="checkbox" checked={verInativos} onChange={(e) => setVerInativos(e.target.checked)} /> ver desativados
        </label>
        {podeEditar && <button className="btn btn-success" onClick={() => abrir(null)}><Plus size={16} /> Novo tipo</button>}
      </div>
      <p style={{ margin: '0 0 1rem', fontSize: '0.85rem', color: 'var(--gray-600)' }}>
        Tudo o que pode virar tarefa no Kanban. Régua (Fraudes), auditorias e o botão "Nova tarefa" só abrem tipos daqui.
        Setor padrão e prioridade valem quando a origem não aponta; o SLA vem do setor.
        {!podeEditar && ' Editar exige nível gestor no Kanban (peça ao administrador).'}
      </p>

      <div style={{ background: '#fff', border: '1px solid var(--gray-200)', borderRadius: 10, overflow: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
          <thead><tr>
            <th style={th}>Tipo</th><th style={th}>Código</th><th style={th}>Setor padrão</th><th style={th}>Prioridade</th>
            <th style={th}>Campos</th><th style={th}>Resultados</th><th style={th}>Manual</th><th style={th} />
          </tr></thead>
          <tbody>
            {tipos.length === 0 && <tr><td colSpan={8} style={{ ...td, color: 'var(--gray-500)' }}>Nenhum tipo cadastrado.</td></tr>}
            {tipos.map((t) => (
              <tr key={t.id} style={{ opacity: t.ativo ? 1 : 0.55 }}>
                <td style={td}><b>{t.nome}</b>{t.descricao && <div style={{ fontSize: '0.8rem', color: 'var(--gray-500)' }}>{t.descricao}</div>}</td>
                <td style={{ ...td, fontFamily: 'monospace', fontSize: '0.8rem' }}>{t.codigo}</td>
                <td style={td}>{t.setor_padrao_nome || <span style={{ color: '#e67e22' }}>(a origem aponta)</span>}</td>
                <td style={td}>{t.prioridade_padrao}</td>
                <td style={td}>{(t.campos || []).map((c) => c.rotulo).join(', ') || '—'}</td>
                <td style={td}>{(t.resultados || []).join(' / ') || '—'}</td>
                <td style={td}>{t.abertura_manual ? 'sim' : 'só por fonte'}</td>
                <td style={{ ...td, whiteSpace: 'nowrap' }}>
                  {podeEditar && (
                    <>
                      <button className="btn btn-secondary" style={{ padding: '2px 8px', marginRight: 4 }} onClick={() => abrir(t)} title="Editar"><Edit3 size={13} /></button>
                      <button className="btn btn-secondary" style={{ padding: '2px 8px' }} onClick={() => alternarAtivo(t)} title={t.ativo ? 'Desativar' : 'Reativar'}><Power size={13} /></button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {form && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: 680 }}>
            <div className="modal-header">
              <h3>{form.id ? 'Editar tipo de tarefa' : 'Novo tipo de tarefa'}</h3>
              <button className="modal-close" onClick={() => setForm(null)}>&times;</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
              <div className="form-group"><label>Nome</label><input className="form-control" value={form.nome} onChange={(e) => set('nome', e.target.value)} /></div>
              <div className="form-group"><label>Código (estável; fontes usam)</label><input className="form-control" value={form.codigo} onChange={(e) => set('codigo', e.target.value)} placeholder="ex.: dados_bancarios" /></div>
            </div>
            <div className="form-group"><label>Descrição</label><input className="form-control" value={form.descricao || ''} onChange={(e) => set('descricao', e.target.value)} /></div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem' }}>
              <div className="form-group"><label>Setor padrão</label>
                <select className="form-control" value={form.setor_padrao_id} onChange={(e) => set('setor_padrao_id', e.target.value)}>
                  <option value="">A origem aponta</option>
                  {setores.map((s) => <option key={s.id} value={s.id}>{s.nome}</option>)}
                </select>
              </div>
              <div className="form-group"><label>Prioridade padrão</label>
                <select className="form-control" value={form.prioridade_padrao} onChange={(e) => set('prioridade_padrao', e.target.value)}>
                  <option value="normal">Normal</option><option value="alta">Alta</option><option value="urgente">Urgente</option>
                </select>
              </div>
              <div className="form-group"><label>Abertura manual</label>
                <select className="form-control" value={form.abertura_manual ? '1' : '0'} onChange={(e) => set('abertura_manual', e.target.value === '1')}>
                  <option value="1">aparece em "Nova tarefa"</option><option value="0">só por fonte</option>
                </select>
              </div>
            </div>
            <div className="form-group">
              <label>Resultados possíveis na finalização (separe por vírgula)</label>
              <input className="form-control" value={form.resultados.join(', ')} onChange={(e) => set('resultados', e.target.value.split(','))} placeholder="ex.: confirmado, legitimo, inconclusivo" />
              <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>É o que volta para quem abriu a tarefa. Vazio = finalização só com o motivo.</div>
            </div>
            <div className="form-group">
              <label>Checklist (um passo por linha)</label>
              <textarea className="form-control" rows="2" value={form.checklist.join('\n')} onChange={(e) => set('checklist', e.target.value.split('\n'))} />
            </div>
            <div className="form-group">
              <label>Campos que o card carrega</label>
              {form.campos.map((c, i) => (
                <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr 0.8fr auto auto', gap: 6, marginBottom: 4, alignItems: 'center' }}>
                  <input className="form-control" placeholder="nome (ex.: valor)" value={c.nome || ''} onChange={(e) => setCampo(i, 'nome', e.target.value)} />
                  <input className="form-control" placeholder="rótulo" value={c.rotulo || ''} onChange={(e) => setCampo(i, 'rotulo', e.target.value)} />
                  <select className="form-control" value={c.tipo || 'text'} onChange={(e) => setCampo(i, 'tipo', e.target.value)}>
                    {TIPOS_CAMPO.map(([v, r]) => <option key={v} value={v}>{r}</option>)}
                  </select>
                  <label style={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}><input type="checkbox" checked={!!c.obrigatorio} onChange={(e) => setCampo(i, 'obrigatorio', e.target.checked)} /> obrig.</label>
                  <button className="btn btn-secondary" style={{ padding: '2px 6px' }} onClick={() => set('campos', form.campos.filter((_, j) => j !== i))}><X size={12} /></button>
                </div>
              ))}
              <button className="btn btn-secondary" style={{ padding: '3px 10px', fontSize: '0.8rem' }} onClick={() => set('campos', [...form.campos, { nome: '', rotulo: '', tipo: 'text', obrigatorio: false }])}>+ campo</button>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setForm(null)} disabled={salvando}>Cancelar</button>
              <button className="btn btn-success" onClick={salvar} disabled={salvando}>{salvando ? 'Salvando…' : 'Salvar'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
